import { Component, Input, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { AuthUser } from '../../../models/auth-user.model';

import { HeaderComponent } from '@coreui/angular';
import { OAuthService } from "angular-oauth2-oidc";
import { environment } from '../../../../environments/environment';
import { AnnouncementService } from '../../../services/announcement.service';
import { Announcement } from '../../../models/announcement.model';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { AuthService } from '../../../auth.service';
import { HucuService } from '../../../services/hucu.service';
import { AnnouncementDialogStateService } from '../../../services/announcement-dialog-state.service';
import { ToastNotificationService } from '../../../services/toast-notification.service';

@Component({
  selector: 'app-default-header',
  templateUrl: './default-header.component.html',
  styleUrls: ['./default-header.component.scss'],
})
export class DefaultHeaderComponent extends HeaderComponent implements OnInit, OnDestroy {

  @Input() sidebarId: string = "sidebar";
  @Output() toggleMessagesSidebar = new EventEmitter<void>();

  currentUser: AuthUser;
  portalSupportUrl = environment.portalSupportUrl;
  unseenAnnouncementsCount: number = 0;
  hucuUnreadCount: number = 0;
  showAnnouncementsDialog: boolean = false;
  unseenAnnouncements: Announcement[] = [];

  private static readonly ACCESS_DENIED_URL = '/access-denied';

  private destroyed$ = new Subject<void>();

  constructor(
    public oauthService: OAuthService,
    public authService: AuthService,
    private readonly announcementService: AnnouncementService,
    private readonly toastNotificationService: ToastNotificationService,
    private readonly hucuService: HucuService,
    private readonly announcementDialogState: AnnouncementDialogStateService,
    private readonly router: Router
  ) {
    super();
  }

  ngOnInit(): void {
    this.currentUser = <AuthUser> this.oauthService.getIdentityClaims();
    this.loadUnseenAnnouncements();
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroyed$)
      )
      .subscribe(event => this.autoOpenAnnouncementsDialogIfAllowed(event.urlAfterRedirects));
    this.hucuService.unreadCount$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(count => {
        this.hucuUnreadCount = count;
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  loadUnseenAnnouncements(): void {
    this.announcementService.getUnseenAnnouncements()
      .pipe(
        takeUntil(this.destroyed$),
        catchError(error => {
          this.toastNotificationService.showError('Failed to load announcements. Please try again later.');
          return of([]);
        })
      )
      .subscribe(announcements => {
        this.unseenAnnouncements = announcements;
        this.unseenAnnouncementsCount = announcements.length;
        this.autoOpenAnnouncementsDialogIfAllowed(this.router.url);
      });
  }

  /**
   * Automatically shows the dialog when there are unseen announcements, unless the
   * user has already dismissed it this session, and never on the Access Denied page.
   * That page is only ever
   * reached by a guard redirect and its sole action is the "Go To Dashboard" button —
   * and while announcements are unseen the dialog is modal with closable,
   * dismissableMask and closeOnEscape all off, so auto-opening it there covers that
   * button with something the user cannot dismiss. Hold it back until they navigate
   * to a page they can actually use; the bell button still opens it on demand, and
   * the unseen badge still shows.
   *
   * A dismissal has to be remembered, because "Later" closes the dialog without
   * clearing unseenAnnouncementsCount — otherwise the next check would force the
   * dialog straight back open. That memory lives in AnnouncementDialogStateService
   * rather than on this component: navigating on or off Access Denied crosses two
   * top-level route configs, which destroys this header and mounts a new one, so a
   * field here is reset by exactly the navigation the dismissal must survive.
   *
   * There is deliberately no "close it again if we land on Access Denied" branch: ''
   * and 'access-denied' are separate top-level routes and the app registers no
   * RouteReuseStrategy, so a guard redirect tears this header down and mounts a fresh
   * one with the dialog already closed. Such a branch could never run today.
   */
  private autoOpenAnnouncementsDialogIfAllowed(url: string): void {
    if (this.announcementDialogState.isDismissed || this.unseenAnnouncementsCount === 0 || this.isOnAccessDeniedPage(url)) {
      return;
    }

    this.showAnnouncementsDialog = true;
  }

  private isOnAccessDeniedPage(url: string): boolean {
    return url.split(/[?#]/)[0] === DefaultHeaderComponent.ACCESS_DENIED_URL;
  }

  openAnnouncementsDialog(): void {
    this.showAnnouncementsDialog = true;
  }

  onAnnouncementsDialogClose(): void {
    this.announcementDialogState.markDismissed();
    this.showAnnouncementsDialog = false;
  }

  onAnnouncementsAcknowledged(acknowledgedAnnouncements: Announcement[]): void {
    this.unseenAnnouncements = [];
    this.unseenAnnouncementsCount = 0;
  }

  logout(): void {
    this.authService.logout()
  }

  generateAvatarColor(): string {
    if (!this.currentUser?.name) {
      return 'hsl(0, 70%, 60%)'; // Default color for null/undefined/empty name
    }
    const hash = this.currentUser.name.split('').reduce((acc, char) => (char.codePointAt(0) ?? 0) + acc, 0);
    const hue = hash % 360; // Ensure hue is within the range [0, 360]
    return `hsl(${hue}, 70%, 60%)`; // Generate HSL color
  }

  getInitials() {
    if (!this.currentUser?.name) {
      return '';
    }
    return this.currentUser.name.split(' ').slice(0, 2).map(name => name[0]).join('').toUpperCase();
  }

  onToggleMessagesSidebar(): void {
    this.toggleMessagesSidebar.emit();
  }

}
